import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/api/client";
import type { InteractionAttachment } from "@/types";

// ---------------------------------------------------------------------------
// Anexos de interação — upload, listagem, exclusão e exibição
// ---------------------------------------------------------------------------

export const TIPOS_ACEITOS = "image/jpeg,image/png,image/webp,application/pdf";
export const TAMANHO_MAXIMO_BYTES = 10 * 1024 * 1024; // igual à API e ao CHECK da sql/019

/**
 * Anexos por cliente: UMA requisição pra todos os anexos das interações daquele
 * investidor, agrupados no componente. O modo por interação existe na API, mas
 * usá-lo na lista faria N+1 requisições.
 */
export function useAttachmentsByClient(clientId?: string) {
  return useQuery({
    queryKey: ["interaction-attachments", "cliente", clientId],
    enabled: !!clientId,
    queryFn: async () => {
      const { data } = await apiClient.get<InteractionAttachment[]>("/interaction-attachments", {
        params: { client_id: clientId },
      });
      return data;
    },
  });
}

export function useUploadAttachment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ file, interactionId }: { file: File; interactionId?: string }) => {
      const form = new FormData();
      form.append("file", file);
      if (interactionId) form.append("interaction_id", interactionId);

      // `Content-Type: undefined` é necessário: o apiClient declara
      // application/json no nível da instância, e se ele sobreviver o servidor
      // não acha o boundary do multipart. Passar undefined faz o axios remover
      // o header e montar o multipart/form-data com boundary correto.
      const { data } = await apiClient.post<InteractionAttachment>(
        "/interaction-attachments",
        form,
        { headers: { "Content-Type": undefined } }
      );
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["interaction-attachments"] }),
  });
}

export function useDeleteAttachment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/interaction-attachments/${id}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["interaction-attachments"] }),
  });
}

// ---------------------------------------------------------------------------
// useAttachmentUrl — o pulo do gato desta feature
// ---------------------------------------------------------------------------
// Uma tag <img src="/api/interaction-attachments/x/content"> NÃO funciona: o
// navegador busca a URL sem cabeçalho nenhum, e a API exige
// `Authorization: Bearer <token>`. Resultado seria 401 e imagem quebrada.
//
// Então o arquivo é buscado por JavaScript (aí o interceptor do apiClient põe o
// token), o corpo vem como Blob e `URL.createObjectURL` gera uma URL `blob:`
// local que a tag <img> consegue carregar. A alternativa seria URL assinada,
// que colocaria a credencial na própria URL — vaza em histórico e log.
//
// O revoke no cleanup não é cosmético: cada objeto URL segura o Blob inteiro na
// memória da aba até ser revogado. Sem isso, abrir e fechar a tela algumas
// vezes com PDFs de vários MB vaza memória de verdade.
export function useAttachmentUrl(id: string | null) {
  const [url, setUrl] = useState<string | null>(null);
  const [erro, setErro] = useState(false);

  useEffect(() => {
    if (!id) {
      setUrl(null);
      return;
    }

    let objectUrl: string | null = null;
    let cancelado = false;
    setErro(false);

    apiClient
      .get<Blob>(`/interaction-attachments/${id}/content`, { responseType: "blob" })
      .then(({ data }) => {
        // Se o componente desmontou durante a busca, revoga na hora em vez de
        // guardar uma URL que ninguém vai usar nem limpar.
        objectUrl = URL.createObjectURL(data);
        if (cancelado) {
          URL.revokeObjectURL(objectUrl);
          return;
        }
        setUrl(objectUrl);
      })
      .catch(() => {
        if (!cancelado) setErro(true);
      });

    return () => {
      cancelado = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [id]);

  return { url, erro };
}

/** Extrai os arquivos aceitos de um evento de colar. Vazio se não houver nenhum. */
export function arquivosDoPaste(e: React.ClipboardEvent): File[] {
  const items = Array.from(e.clipboardData?.items ?? []);
  return items
    .filter((it) => it.kind === "file")
    .map((it) => it.getAsFile())
    .filter((f): f is File => f !== null);
}
